import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getUserProfileApi, updateUserProfileApi, changeUserMpinApi } from "../api/api";

const CURRENT_MPIN = "1234";

interface ProfileData {
  name: string;
  mobile: string;
  email: string;
  shop: string;
  city: string;
  state: string;
  pincode: string;
    mpin: string;
}

interface Toast {
  msg: string;
  type: "success" | "error";
  visible: boolean;
}

function useToast() {
  const [toast, setToast] = useState<Toast>({ msg: "", type: "success", visible: false });
  const show = (msg: string, type: "success" | "error") => {
    setToast({ msg, type, visible: true });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3000);
  };
  return { toast, show };
}

function ToastMsg({ toast }: { toast: Toast }) {
  if (!toast.visible) return null;
  const isSuccess = toast.type === "success";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      padding: "10px 14px", borderRadius: 6, fontSize: 13, marginTop: 12,
      background: isSuccess ? "#eaf3de" : "#fcebeb",
      color: isSuccess ? "#3b6d11" : "#a32d2d",
      border: `1px solid ${isSuccess ? "#97c459" : "#f09595"}`,
    }}>
      <span>{isSuccess ? "✓" : "✕"}</span>
      <span>{toast.msg}</span>
    </div>
  );
}

function SectionTitle({ label, color }: { label: string; color?: string }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 600, color: color || "#6b7280",
      textTransform: "uppercase", letterSpacing: "0.07em",
      marginBottom: 16, display: "flex", alignItems: "center", gap: 8,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: color || "#6b7280", display: "inline-block" }} />
      {label}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  height: 38, padding: "0 12px", fontSize: 14,
  border: "1px solid #e5e7eb", borderRadius: 6,
  background: "#fff", color: "#111827", outline: "none", width: "100%",
  boxSizing: "border-box",
};

const readonlyInputStyle: React.CSSProperties = {
  ...inputStyle, background: "#f9fafb", color: "#6b7280", cursor: "default",
};

function MpinInput({ id, placeholder, value, onChange }: {
  id: string; placeholder: string; value: string; onChange: (v: string) => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <input
        id={id}
        type={show ? "text" : "password"}
        maxLength={4}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
        style={{ ...inputStyle, paddingRight: 38, letterSpacing: show ? "0.1em" : "0.2em" }}
      />
      <button
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Hide MPIN" : "Show MPIN"}
        style={{
          position: "absolute", right: 10, background: "none", border: "none",
          cursor: "pointer", color: "#9ca3af", padding: 0, fontSize: 16, lineHeight: 1,
        }}
      >
        {show ? "🙈" : "👁"}
      </button>
    </div>
  );
}

export default function ViewProfilePage() {
  const { user, isHydrated } = useAuth();
  const [profile, setProfile] = useState<ProfileData>({
    name: "",
    mobile: "",
    email: "",
    shop: "",
    city: "",
    state: "",
    pincode: "",
    mpin: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const profileToast = useToast();
  const [mpin, setMpin] = useState({ old: "", new: "", confirm: "" });
  const mpinToast = useToast();
  const [saving, setSaving] = useState(false);
  const [changingMpin, setChangingMpin] = useState(false);

  const cardStyle: React.CSSProperties = {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "28px 32px",
    marginBottom: "24px",
  };

  const divider = <div style={{ height: "1px", background: "#f3f4f6", margin: "24px 0" }} />;

  const grid3 = (children: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 24px" }}>
      {children}
    </div>
  );

  const grid4 = (children: React.ReactNode) => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px 24px" }}>
      {children}
    </div>
  );

  function handleProfileChange(key: keyof ProfileData, val: string) {
    setProfile((p) => ({ ...p, [key]: val }));
  }

  async function saveProfile() {
    if (!profile.name.trim() || !profile.mobile.trim()) {
      profileToast.show("Name and mobile are required.", "error");
      return;
    }

    if (!user?.id || !user?.token) {
      profileToast.show("Not authenticated.", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: profile.name,
        mobile_no: profile.mobile,
        email: profile.email,
        shop_name: profile.shop,
        shop_address: profile.city,
        address: profile.state,
        pincode: profile.pincode,
      };

      const body = await updateUserProfileApi(user.id, payload, user.token);

      profileToast.show(body?.message || "Profile updated successfully.", "success");
    } catch (err: any) {
      profileToast.show(err?.message || "Failed to update profile.", "error");
    } finally {
      setSaving(false);
    }
  }

  async function changeMpin() {
    if (mpin.old.length < 4 || mpin.new.length < 4 || mpin.confirm.length < 4) {
      mpinToast.show("All MPIN fields must be 4 digits.", "error");
      return;
    }

    if (mpin.new !== mpin.confirm) {
      mpinToast.show("New MPIN and confirm MPIN do not match.", "error");
      return;
    }

    if (!user?.id || !user?.token) {
      mpinToast.show("Not authenticated.", "error");
      return;
    }

    setChangingMpin(true);
    try {
      const body = await changeUserMpinApi(user.id, mpin.old, mpin.new, user.token);
      mpinToast.show(body?.message || "MPIN changed successfully.", "success");
      setMpin({ old: "", new: "", confirm: "" });
      setProfile((p) => ({ ...p, mpin: mpin.new }));
    } catch (err: any) {
      mpinToast.show(err?.message || "Failed to change MPIN.", "error");
    } finally {
      setChangingMpin(false);
    }
  }

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const loadProfile = async () => {
      if (!user?.id || !user?.token) {
        setError("Not authenticated");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const body = await getUserProfileApi(user.id, user.token);
        const userData = body.data?.user ?? body.data ?? body;

        setProfile({
          name: userData.name ?? "",
          mobile: userData.mobile_no ?? userData.mobile ?? "",
          email: userData.email ?? "",
          shop: userData.shop_name ?? userData.shop ?? "",
          city: userData.shop_address ?? "",
          state: userData.address ?? "",
          pincode: "",
          mpin: userData.mpin ?? "",
        });
        // initialize current mpin input with fetched mpin (keeps masked in input)
        setMpin((m) => ({ ...m, old: userData.mpin ?? "" }));
      } catch (err: any) {
        setError(err?.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [user, isHydrated]);

  if (loading) {
    return (
      <div style={{ padding: "28px 32px", minHeight: "100vh", fontFamily: "'Inter', 'Segoe UI', sans-serif", background: "#f9fafb" }}>
        <div style={{ padding: 24, background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          Loading profile...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "28px 32px", minHeight: "100vh", fontFamily: "'Inter', 'Segoe UI', sans-serif", background: "#f9fafb" }}>
        <div style={{ padding: 24, background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", color: "#b91c1c" }}>
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "28px 32px", fontFamily: "'Inter', 'Segoe UI', sans-serif", width: "100%", boxSizing: "border-box", background: "#f9fafb", minHeight: "100vh" }}>

      {/* Profile Card */}
      <div style={cardStyle}>

        
        {/* Personal Info */}
        <SectionTitle label="Personal info" color="#3b82f6" />
        {grid4(<>
          <Field label="Full name">
            <input style={inputStyle} value={profile.name}
              onChange={(e) => handleProfileChange("name", e.target.value)} />
          </Field>
          <Field label="Mobile number">
            <input style={inputStyle} value={profile.mobile}
              onChange={(e) => handleProfileChange("mobile", e.target.value)} />
          </Field>
          <Field label="Email address">
            <input style={inputStyle} type="email" value={profile.email}
              onChange={(e) => handleProfileChange("email", e.target.value)} />
          </Field>
        </>)}
        <div style={{ marginTop: 16 }}>
          <Field label="Shop name">
            <input
              style={{ ...inputStyle, maxWidth: "33.33%" }}
              value={profile.shop}
              onChange={(e) => handleProfileChange("shop", e.target.value)}
            />
          </Field>
        </div>

        {divider}

        {/* Address */}
        <SectionTitle label="Address" color="#ec4899" />
        {grid3(<>
          <Field label="City">
            <input style={inputStyle} value={profile.city}
              onChange={(e) => handleProfileChange("city", e.target.value)} />
          </Field>
          <Field label="State">
            <input style={inputStyle} value={profile.state}
              onChange={(e) => handleProfileChange("state", e.target.value)} />
          </Field>
          <Field label="Pincode">
            <input style={inputStyle} value={profile.pincode}
              onChange={(e) => handleProfileChange("pincode", e.target.value)} />
          </Field>
        </>)}

        {divider}

        {/* Account Info */}
        {/* Account Info 
        <SectionTitle label="Account info" color="#6366f1" />
        {grid3(<>
          <Field label="Agent ID">
            <input style={readonlyInputStyle} value="2102865" readOnly />
          </Field>
          <Field label="KYC status">
            <input style={readonlyInputStyle} value="Verified" readOnly />
          </Field>
          <Field label="Balance (INR)">
            <input style={readonlyInputStyle} value="₹65,387.33" readOnly />
          </Field>
        </>)}
      */}
        {/* Save Button */}
        <div style={{ marginTop: 24 }}>
          <button
            onClick={saveProfile}
            disabled={saving}
            style={{
              height: 40, padding: "0 24px", fontSize: 14, fontWeight: 500,
              background: "#2563eb", color: "#fff", border: "none",
              borderRadius: 6, cursor: "pointer", display: "inline-flex",
              alignItems: "center", gap: 8,
            }}
          >
            {saving ? "Saving..." : "💾 Update profile"}
          </button>
          <ToastMsg toast={profileToast.toast} />
        </div>
      </div>

      {/* MPIN Card */}
      <div style={cardStyle}>
        <SectionTitle label="Change MPIN" color="#f59e0b" />
        <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 20, marginTop: -8 }}>
          Enter your current MPIN and a new 4-digit MPIN to update.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "16px 24px", alignItems: "flex-end" }}>
          <div>
            <label style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500, display: "block", marginBottom: 5 }}>
              Current MPIN
            </label>
            <MpinInput id="mpin-old" placeholder="" value={mpin.old}
              onChange={(v) => setMpin((m) => ({ ...m, old: v }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500, display: "block", marginBottom: 5 }}>
              New MPIN
            </label>
            <MpinInput id="mpin-new" placeholder="" value={mpin.new}
              onChange={(v) => setMpin((m) => ({ ...m, new: v }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 500, display: "block", marginBottom: 5 }}>
              Confirm new MPIN
            </label>
            <MpinInput id="mpin-confirm" placeholder="" value={mpin.confirm}
              onChange={(v) => setMpin((m) => ({ ...m, confirm: v }))} />
          </div>
          <button
            onClick={changeMpin}
            disabled={changingMpin}
            style={{
              height: 38, padding: "0 20px", fontSize: 13, fontWeight: 500,
              background: "#2563eb", color: "#fff", border: "none",
              borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            {changingMpin ? "Changing..." : "🔄 Change MPIN"}
          </button>
        </div>

        <ToastMsg toast={mpinToast.toast} />
      </div>
    </div>
  );
}